import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import TouchpointMonthCard from './components/TouchpointMonthCard';
// import GoogleReviewPanel from './components/GoogleReviewPanel'; // temporarily hidden
import RelationshipTriggersPanel from './components/RelationshipTriggersPanel';
import PreviewTestSmsModal from '../../components/PreviewTestSmsModal';
import TouchpointImageUploader from '../../components/touchpoint-media/TouchpointImageUploader';
import { getAuthHeaders } from '../../lib/api';
import { DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE } from '../../../lib/sms/message-formatting.js';

const EvergreenTouchpoints = () => {
  const { user, userProfile, reloadProfile } = useAuth();
  const [touchpoints, setTouchpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [triggerAttempted, setTriggerAttempted] = useState(false);

  // Touchpoints pause state
  const [touchpointsPaused, setTouchpointsPaused] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [reviewProfile, setReviewProfile] = useState(null);
  const [firstTouchImageUrl, setFirstTouchImageUrl] = useState(null);
  const [firstTouchIntro, setFirstTouchIntro] = useState(DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE);
  const [firstTouchIsCustom, setFirstTouchIsCustom] = useState(false);
  const [firstTouchSaving, setFirstTouchSaving] = useState(false);
  const [firstTouchSaveMsg, setFirstTouchSaveMsg] = useState('');
  const [showPreviewTest, setShowPreviewTest] = useState(false);

  useEffect(() => {
    if (user) {
      fetchTouchpoints();
      loadPauseAndReviewState();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadPauseAndReviewState = async () => {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select(
          'touchpoints_paused, google_business_url, google_review_message_template, google_review_followup_template, google_review_auto_on_new_contact, google_review_status, google_review_progress_sent, google_review_progress_total, google_review_last_blast_at, first_contact_intro_template, first_contact_image_url, phone, full_name'
        )
        .eq('id', user.id)
        .single();
      if (profile) {
        setTouchpointsPaused(profile.touchpoints_paused ?? false);
        setReviewProfile(profile);
        setFirstTouchImageUrl(profile.first_contact_image_url || null);
        if (profile.first_contact_intro_template) {
          setFirstTouchIntro(profile.first_contact_intro_template);
          setFirstTouchIsCustom(true);
        } else {
          setFirstTouchIntro(DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE);
          setFirstTouchIsCustom(false);
        }
      }
    } catch (err) {
      // Column may be missing before migration — fall back without image fields
      console.error('Failed to load pause/review state:', err);
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select(
            'touchpoints_paused, google_business_url, google_review_message_template, google_review_followup_template, google_review_auto_on_new_contact, google_review_status, google_review_progress_sent, google_review_progress_total, google_review_last_blast_at, first_contact_intro_template, phone, full_name'
          )
          .eq('id', user.id)
          .single();
        if (profile) {
          setTouchpointsPaused(profile.touchpoints_paused ?? false);
          setReviewProfile(profile);
          if (profile.first_contact_intro_template) {
            setFirstTouchIntro(profile.first_contact_intro_template);
            setFirstTouchIsCustom(true);
          } else {
            setFirstTouchIntro(DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE);
            setFirstTouchIsCustom(false);
          }
        }
      } catch (fallbackErr) {
        console.error('Failed to load pause/review state (fallback):', fallbackErr);
      }
    }
  };

  const handleToggleTouchpointsPause = async () => {
    const newPaused = !touchpointsPaused;
    setPauseLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/touchpoints-pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ paused: newPaused }),
      });
      if (res.ok) {
        setTouchpointsPaused(newPaused);
      }
    } catch (err) {
      console.error('Failed to toggle touchpoints pause:', err);
    } finally {
      setPauseLoading(false);
    }
  };

  const parseJsonSafe = async (response) => {
    const text = await response.text();
    if (!text || text.trim() === '') return null;
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`Invalid response from server (expected JSON). Status: ${response.status}`);
    }
  };

  const fetchTouchpoints = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch('/api/touchpoints/view', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await parseJsonSafe(response);

      if (!response.ok) {
        const msg = data?.details ? `${data.error}: ${data.details}` : (data?.error || `Failed to fetch touchpoints (${response.status})`);
        throw new Error(msg);
      }

      const fetched = data?.data ?? [];
      setTouchpoints(fetched);
      setStats(data?.stats ?? null);

      // Fallback: if view window is incomplete, trigger generation (skipped onboarding, partial regen, etc.)
      if (fetched.length < 3 && user?.id && userProfile?.full_name && !triggerAttempted) {
        setTriggerAttempted(true);
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const triggerRes = await fetch('/api/trigger-onboarding', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ agentId: user.id, agentName: userProfile.full_name })
            });
            if (triggerRes.ok) {
              await fetchTouchpoints();
            }
          }
        } catch (triggerErr) {
          console.warn('Fallback touchpoint generation failed:', triggerErr);
        }
      }
    } catch (err) {
      console.error('Error fetching touchpoints:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageUpdate = async (touchpointId, newMessageText) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`/api/touchpoints/${touchpointId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messageText: newMessageText })
      });

      const data = await parseJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.error || `Failed to update message (${response.status})`);
      }

      // Update local state
      setTouchpoints(prev => 
        prev.map(tp => 
          tp.id === touchpointId 
            ? { ...tp, messageText: newMessageText, complianceStatus: 'compliant' }
            : tp
        )
      );

      return { success: true, data: data?.data ?? null };
    } catch (err) {
      console.error('Error updating message:', err);
      return { success: false, error: err.message };
    }
  };

  const handleImageUpdate = async (touchpointId, imageUrl) => {
    // URL is already persisted by POST /api/touchpoints/upload-image — only sync local UI.
    setTouchpoints((prev) =>
      prev.map((tp) => (tp.id === touchpointId ? { ...tp, imageUrl } : tp))
    );
    return { success: true };
  };

  const handleFirstTouchImageSaved = async (url) => {
    // URL is already persisted by upload-image for purpose=first-touch
    setFirstTouchImageUrl(url);
    if (reloadProfile) {
      try {
        await reloadProfile();
      } catch {
        // non-blocking
      }
    }
  };

  const handleSaveFirstTouchIntro = async () => {
    setFirstTouchSaving(true);
    setFirstTouchSaveMsg('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/profile-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          firstContactIntroTemplate: firstTouchIsCustom ? firstTouchIntro : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to save intro');
      if (reloadProfile) {
        try {
          await reloadProfile();
        } catch {
          // non-blocking
        }
      }
      setFirstTouchSaveMsg('First-touch intro saved.');
    } catch (err) {
      setFirstTouchSaveMsg(`Failed to save: ${err.message}`);
    } finally {
      setFirstTouchSaving(false);
    }
  };

  const handleResetFirstTouchIntro = () => {
    setFirstTouchIntro(DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE);
    setFirstTouchIsCustom(false);
    setFirstTouchSaveMsg('');
  };

  const canPreviewTest = Boolean(
    touchpoints.some((tp) => tp?.messageText?.trim()) || firstTouchIntro
  );

  const handleComplianceCheck = async (messageText) => {
    console.log('=== COMPLIANCE CHECK CALLED ===');
    console.log('Message text:', messageText);
    
    try {
      console.log('Making compliance API call to: /api/touchpoints/compliance-check');
      const response = await fetch('/api/touchpoints/compliance-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messageText })
      });

      console.log('Compliance API response status:', response.status);
      const data = await parseJsonSafe(response);
      if (data === null) {
        return { compliant: false, issues: ['Empty response'], suggestions: ['Please try again'] };
      }
      console.log('Compliance API response data:', data);
      
      return data?.compliance ?? { compliant: false, issues: ['Invalid response'], suggestions: ['Please try again'] };
    } catch (err) {
      console.error('Error checking compliance:', err);
      return {
        compliant: false,
        issues: ['Unable to verify compliance'],
        suggestions: ['Please review manually']
      };
    }
  };

  const handleRegenerateMessage = async (touchpointId, monthYear) => {
    console.log('=== HANDLE REGENERATE MESSAGE CALLED ===');
    console.log('touchpointId:', touchpointId);
    console.log('monthYear:', monthYear);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('ERROR: No active session');
        throw new Error('No active session');
      }
      console.log('Session found, access token length:', session.access_token.length);

      console.log('Making API call to:', `/api/touchpoints-regenerate`);
      console.log('With touchpointId:', touchpointId, 'monthYear:', monthYear);
      
      const response = await fetch(`/api/touchpoints-regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ touchpointId, monthYear })
      });

      console.log('API response status:', response.status);
      console.log('API response headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await parseJsonSafe(response);

      if (!response.ok) {
        console.log('API call failed with status:', response.status);
        const detail = data?.details || data?.message;
        throw new Error(
          detail ? `${data?.error || 'Request failed'}: ${detail}` : (data?.error || `Failed to regenerate message (${response.status})`)
        );
      }
      console.log('API response data:', data);

      if (!data?.data) {
        throw new Error('Invalid response: missing data');
      }

      console.log('API call successful, updating local state...');
      // Update local state
      setTouchpoints(prev => 
        prev.map(tp => 
          tp.id === touchpointId 
            ? { 
                ...tp, 
                messageText: data.data.messageText,
                complianceStatus: data.data.complianceStatus,
                complianceIssues: data.data.complianceIssues,
                complianceSuggestions: data.data.complianceSuggestions,
                personalityTypeUsed: data.data.personalityTypeUsed,
                sendAfterDate: data.data.sendAfterDate ?? tp.sendAfterDate,
                sendBeforeDate: data.data.sendBeforeDate ?? tp.sendBeforeDate,
                isHolidayMessage: data.data.isHolidayMessage ?? tp.isHolidayMessage,
              }
            : tp
        )
      );

      console.log('Local state updated, returning success');
      return { success: true, data: data.data };
    } catch (err) {
      console.error('Error in handleRegenerateMessage:', err);
      return { success: false, error: err.message };
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Evergreen Touchpoints - Nurture Buddy.ai</title>
          <meta name="description" content="AI-generated monthly messages to keep you top-of-mind with past clients" />
        </Helmet>
        
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-16 pb-20 md:pb-0">
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading touchpoints...</p>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Evergreen Touchpoints - Nurture Buddy.ai</title>
          <meta name="description" content="AI-generated monthly messages to keep you top-of-mind with past clients" />
        </Helmet>
        
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-16 pb-20 md:pb-0">
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center max-w-md px-4">
                <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={32} className="text-error" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Touchpoints</h2>
                <p className="text-muted-foreground mb-6">{error}</p>
                <button 
                  onClick={fetchTouchpoints}
                  className="bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-smooth touch-target"
                >
                  Try Again
                </button>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Evergreen Touchpoints - Nurture Buddy.ai</title>
        <meta name="description" content="AI-generated monthly messages to keep you top-of-mind with past clients" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Calendar" size={24} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">
                      Evergreen Touchpoints
                    </h1>
                    <p className="text-muted-foreground mt-0.5">
                      AI-generated monthly messages to keep you top-of-mind with past clients
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground leading-snug max-w-lg">
                      New contacts get a first-touch intro in place of that month&apos;s evergreen
                      message, then monthly messages continue. Edit the intro below.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setShowPreviewTest(true)}
                    disabled={!canPreviewTest}
                    title={!canPreviewTest ? 'No message available to preview yet' : 'Preview and send a test SMS to yourself'}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                  >
                    <Icon name="Eye" size={16} />
                    Preview &amp; Test
                  </button>

                  {/* Touchpoints pause/resume button */}
                  <button
                    onClick={handleToggleTouchpointsPause}
                    disabled={pauseLoading}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 ${
                      touchpointsPaused
                        ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                        : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    {pauseLoading ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : touchpointsPaused ? (
                      <Icon name="Play" size={16} />
                    ) : (
                      <Icon name="Pause" size={16} />
                    )}
                    {touchpointsPaused ? 'Resume Touchpoints' : 'Pause Touchpoints'}
                  </button>
                </div>
              </div>

              {touchpointsPaused && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm mb-4">
                  <Icon name="PauseCircle" size={16} />
                  Touchpoints are paused. Automated monthly messages will not be sent until you resume.
                </div>
              )}
              
              {stats && (
                <div className="flex gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={16} />
                    <span>Total Clients: {stats.totalClients}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} />
                    <span>Eligible: {stats.eligibleClients}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Touchpoint Cards */}
            {touchpoints.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {touchpoints.map((touchpoint, index) => (
                  <TouchpointMonthCard
                    key={touchpoint.monthYear || index}
                    touchpoint={touchpoint}
                    onMessageUpdate={handleMessageUpdate}
                    onComplianceCheck={handleComplianceCheck}
                    onRegenerateMessage={handleRegenerateMessage}
                    onImageUpdate={handleImageUpdate}
                    isCurrentMonth={touchpoint.isCurrentMonth}
                    progressInfo={touchpoint.progressInfo}
                  />
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="flex items-center justify-center h-48">
                <div className="text-center max-w-md px-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Calendar" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">No Touchpoints Yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Your AI-generated monthly touchpoints will appear here once they're created.
                  </p>
                  <button 
                    onClick={fetchTouchpoints}
                    className="bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-smooth touch-target"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            )}

            <div className="mt-8 rounded-xl border border-border bg-card p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="MessageSquare" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">First-touch intro</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Sent once to new contacts instead of that month&apos;s evergreen message. Use{' '}
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{first_name}'}</code> and{' '}
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{agent_name}'}</code>.
                  </p>
                </div>
              </div>

              <textarea
                className="w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                value={firstTouchIntro}
                onChange={(e) => {
                  setFirstTouchIntro(e.target.value);
                  setFirstTouchIsCustom(true);
                  setFirstTouchSaveMsg('');
                }}
                placeholder={DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE}
              />

              <div className="flex flex-wrap items-center justify-between gap-2 mt-2 mb-4">
                <p className="text-xs text-muted-foreground">
                  {firstTouchIsCustom ? 'Custom message' : 'Using default message'}
                  {firstTouchSaveMsg ? (
                    <span
                      className={
                        firstTouchSaveMsg.startsWith('Failed')
                          ? ' text-red-600'
                          : ' text-green-700'
                      }
                    >
                      {' · '}
                      {firstTouchSaveMsg}
                    </span>
                  ) : null}
                </p>
                <div className="flex items-center gap-3">
                  {firstTouchIsCustom && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={handleResetFirstTouchIntro}
                    >
                      Reset to default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveFirstTouchIntro}
                    disabled={firstTouchSaving || !firstTouchIntro.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {firstTouchSaving ? (
                      <Icon name="Loader2" size={14} className="animate-spin" />
                    ) : (
                      <Icon name="Save" size={14} />
                    )}
                    Save intro
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-1">Intro photo (optional)</p>
                <p className="text-xs text-muted-foreground mb-3">
                  A casual selfie (smile + wave or thumbs-up) sent with the first SMS.
                </p>
                {user?.id && (
                  <TouchpointImageUploader
                    imageUrl={firstTouchImageUrl}
                    purpose="first-touch"
                    alignHint="a friendly hello"
                    showCameraOption
                    description="Best: you smiling and waving, or giving a thumbs-up — warm and personal, not a headshot. Compressed automatically for MMS."
                    onSaved={handleFirstTouchImageSaved}
                  />
                )}
              </div>
            </div>

            <RelationshipTriggersPanel />

            {/* Google Review / Review Ask — temporarily hidden from UI
            {reviewProfile !== null && (
              <GoogleReviewPanel initialProfile={reviewProfile} />
            )}
            */}
          </div>
        </main>
      </div>

      <PreviewTestSmsModal
        open={showPreviewTest}
        onClose={() => setShowPreviewTest(false)}
        touchpoints={touchpoints}
        firstTouchIntro={firstTouchIntro}
        firstTouchImageUrl={firstTouchImageUrl}
        agentPhone={reviewProfile?.phone || userProfile?.phone}
        agentName={reviewProfile?.full_name || userProfile?.full_name}
        gmbUrl={reviewProfile?.google_business_url}
      />
    </>
  );
};

export default EvergreenTouchpoints;
